import React from 'react';
import { Input, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import ChevronDownIcon from 'assets/chevron_down.svg?react';
import ChevronUpIcon from 'assets/chevron_up.svg?react';
import FormSectionHeader from 'screens/Applications&WorkOrders/StepsLayout/Components/ProductsStep/components/FillProductDetailsDrawer/components/FormSectionHeader';
import { getShortUnitLabel } from 'helpers/unitsAbbreviation';
import ListPriceControls from './components/ListPriceControls';

export interface ProductCostSectionProps {
  priceValue: number;
  priceUnit: string;
  totalCostValue: number;
  totalQuantityUnit: string;
  units: Array<{ key: string }>;
  unitsAreIncompatible: boolean;
  isResetDisabled: boolean;
  canSaveNewListPrice: boolean;
  saveAsNewListPrice: boolean;
  setSaveAsNewListPrice: (checked: boolean) => void;
  isPriceUnitDropdownOpen: boolean;
  setIsPriceUnitDropdownOpen: (open: boolean) => void;
  getCostDisplay: (field: 'price' | 'cost', value: number) => string;
  onCostFocus: (field: 'price' | 'cost') => () => void;
  onCostChange: (
    field: 'price' | 'cost'
  ) => (e: React.ChangeEvent<HTMLInputElement>) => void;
  onCostBlur: (field: 'price' | 'cost') => () => void;
  onPriceUnitChange: (unit: string) => void;
  onResetPrice: () => void;
}

/**
 * Presentational half of the drawer's Cost section. Holds no state of its own:
 * the drawer owns the quantity/price/cost arithmetic and passes down both the
 * values and the handlers, so the three fields stay derived in one place.
 *
 * Rendered only where the user may write costs — the drawer keeps that gate, so
 * this component never has to reason about permissions.
 */
const ProductCostSection: React.FC<ProductCostSectionProps> = ({
  priceValue,
  priceUnit,
  totalCostValue,
  totalQuantityUnit,
  units,
  unitsAreIncompatible,
  isResetDisabled,
  canSaveNewListPrice,
  saveAsNewListPrice,
  setSaveAsNewListPrice,
  isPriceUnitDropdownOpen,
  setIsPriceUnitDropdownOpen,
  getCostDisplay,
  onCostFocus,
  onCostChange,
  onCostBlur,
  onPriceUnitChange,
  onResetPrice
}) => (
  <section className="mb-6">
    <FormSectionHeader title="Cost" />
    <div className="flex flex-col gap-4 mt-3 cwf-invoice-product-drawer-form-content">
      {/* FR-105: auto-populated "0.00" default with the selected
            product's unit of measure rendered as an in-input suffix. */}
      <ListPriceControls
        isResetDisabled={isResetDisabled}
        onReset={onResetPrice}
        isCheckboxDisabled={!canSaveNewListPrice || unitsAreIncompatible}
        checked={saveAsNewListPrice}
        onCheckedChange={setSaveAsNewListPrice}
      >
        <Input
          label="Price per Unit"
          type="text"
          inputMode="decimal"
          value={getCostDisplay('price', priceValue)}
          onFocus={onCostFocus('price')}
          onChange={onCostChange('price')}
          onBlur={onCostBlur('price')}
          prefix="$"
          suffix="per"
          data-testid="fill-product-price-per-unit"
          addonAfter={
            <Select
              showSearch
              placeholder="Select unit"
              value={priceUnit || undefined}
              onChange={onPriceUnitChange}
              className="custom-select"
              status={unitsAreIncompatible ? 'error' : undefined}
              data-testid="fill-product-price-unit"
              suffixIcon={
                isPriceUnitDropdownOpen ? (
                  <ChevronUpIcon width={18} height={18} />
                ) : (
                  <ChevronDownIcon width={18} height={18} />
                )
              }
              onDropdownVisibleChange={setIsPriceUnitDropdownOpen}
              filterOption={(input: string, option: any) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {units.map(unit => (
                <Select.Option
                  key={`price-${unit.key}`}
                  value={unit.key}
                  label={getShortUnitLabel(unit.key)}
                >
                  {getShortUnitLabel(unit.key)}
                </Select.Option>
              ))}
            </Select>
          }
        />
        {unitsAreIncompatible && (
          <div
            className="text-xs text-remove mt-1"
            data-testid="fill-product-price-unit-error"
          >
            {`Cannot convert ${getShortUnitLabel(
              totalQuantityUnit
            )} to ${getShortUnitLabel(
              priceUnit
            )} for this product, so the Total Cost cannot be worked out.`}
          </div>
        )}
      </ListPriceControls>

      <Input
        label="Total Cost"
        type="text"
        inputMode="decimal"
        value={getCostDisplay('cost', totalCostValue)}
        onFocus={onCostFocus('cost')}
        onChange={onCostChange('cost')}
        onBlur={onCostBlur('cost')}
        prefix="$"
        data-testid="fill-product-cost"
      />
    </div>
  </section>
);

export default ProductCostSection;
